import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { useLocations } from "@/context/LocationContext";
import { toast } from "sonner";
import { Plus, ChefHat, Flame, CheckCircle2, ListTree, Bluetooth, Pencil, Trash2, ScanLine, XCircle } from "lucide-react";
import SupervisorPinModal from "@/components/SupervisorPinModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader, Panel, StaffInitials } from "@/components/ui-bits";
import DateScopePicker, { scopeToParams, downloadCsv } from "@/components/DateScopePicker";
import { StageBadge } from "@/pages/Dashboard";
import BarcodeScanner from "@/components/BarcodeScanner";
import { CarryOverPanel } from "@/components/CarryOverPanel";
import PrintRecipeButton from "@/components/production/PrintRecipeButton";
import { devLog } from "@/lib/devLog";

const SCAN_TONE = {
  success: { box: "bg-emerald-50 border border-emerald-300 text-emerald-900", dot: "bg-emerald-500" },
  warn: { box: "bg-amber-50 border border-amber-300 text-amber-900", dot: "bg-amber-500" },
  error: { box: "bg-rose-50 border border-rose-300 text-rose-900", dot: "bg-rose-500" },
};

function rowTone(isDone, isScanning) {
  if (isDone) return "border-emerald-200 bg-emerald-50/40";
  if (isScanning) return "border-amber-300 bg-amber-50/40 ring-1 ring-amber-300";
  return "border-border bg-white dark:bg-stone-900";
}

// ---------------------------------------------------------------------------
// Sub-components (kept colocated for cohesion; each owns its own concern)
// ---------------------------------------------------------------------------

// True when every ingredient on the batch's recipe has at least one matching
// entry in `batch.consumptions` (matched by material_id). Drives the green
// state of the *Ingredients* action button on the batch row.
function consumeComplete(batch, recipe) {
  if (!recipe || !Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) {
    return false;
  }
  const consumed = new Set(
    (batch.consumptions || []).map((c) => c.material_id).filter(Boolean)
  );
  return recipe.ingredients.every((ing) => consumed.has(ing.material_id));
}

function BatchesTable({ batches, recipes, onStage, onEdit, onDelete }) {
  if (batches.length === 0) {
    return (
      <div className="py-10 text-center text-stone-500">
        No batches yet. Start your first production batch.
      </div>
    );
  }
  return (
    <table className="w-full" data-testid="batches-table">
      <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
        <tr>
          <th className="py-3">Code</th>
          <th>Recipe</th>
          <th>Stage</th>
          <th className="text-right">Planned</th>
          <th className="text-right">Produced</th>
          <th>Started</th>
          <th>By</th>
          <th className="text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {batches.map((b) => {
          const recipe = recipes.find((r) => r.id === b.recipe_id);
          const ingredientsDone = consumeComplete(b, recipe);
          return (
          <tr key={b.id} className="border-t border-border h-16">
            <td className="font-mono text-sm">{b.batch_code}</td>
            <td className="font-medium">{b.recipe_name}</td>
            <td><StageBadge stage={b.stage} /></td>
            <td className="text-right tnum">{fmt2(b.quantity_planned)}</td>
            <td className="text-right tnum font-semibold">{fmt2(b.quantity_produced)}</td>
            <td className="text-stone-600 text-sm">
              {b.started_at?.slice(0, 16).replace("T", " ")}
            </td>
            <td>
              <StaffInitials
                name={b.created_by_name}
                testId={`batch-by-${b.id}`}
              />
            </td>
            <td className="text-right space-x-1">
              <Button
                size="sm"
                variant={ingredientsDone ? "default" : "outline"}
                className={
                  ingredientsDone
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent"
                    : ""
                }
                onClick={() => onStage(b, "consume")}
                data-testid={`consume-${b.id}`}
                data-complete={ingredientsDone ? "true" : "false"}
                title={ingredientsDone ? "All ingredients recorded — click to review" : "Record ingredient usage"}
              >
                {ingredientsDone ? (
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                ) : (
                  <ListTree className="h-4 w-4 mr-1" />
                )}
                Ingredients
              </Button>
              <Button size="sm" variant="outline" onClick={() => onStage(b, "bake")} data-testid={`bake-${b.id}`}>
                <Flame className="h-4 w-4 mr-1" /> Bake
              </Button>
              <Button size="sm" onClick={() => onStage(b, "complete")} data-testid={`complete-${b.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
              </Button>
              <Button size="icon" variant="ghost" title="Edit batch (PIN locked)" onClick={() => onEdit(b)} data-testid={`edit-${b.id}`}>
                <Pencil className="h-4 w-4 text-stone-600" />
              </Button>
              <Button size="icon" variant="ghost" title="Delete batch (PIN locked)" onClick={() => onDelete(b)} data-testid={`delete-${b.id}`}>
                <Trash2 className="h-4 w-4 text-rose-600" />
              </Button>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function EditBatchView({ batch, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    quantity_planned: String(batch.quantity_planned ?? ""),
    quantity_produced: String(batch.quantity_produced ?? ""),
    notes: batch.notes || "",
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <Pencil className="h-5 w-5" /> Edit batch · {batch.batch_code}
        </DialogTitle>
      </DialogHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            quantity_planned: form.quantity_planned === "" ? null : Number(form.quantity_planned),
            quantity_produced: form.quantity_produced === "" ? null : Number(form.quantity_produced),
            notes: form.notes,
          });
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Planned quantity</Label>
            <Input
              type="number" step="0.01" min="0" className="h-12 mt-1"
              value={form.quantity_planned}
              onChange={(e) => setForm({ ...form, quantity_planned: e.target.value })}
              data-testid="edit-batch-planned"
            />
          </div>
          <div>
            <Label>Produced quantity</Label>
            <Input
              type="number" step="0.01" min="0" className="h-12 mt-1"
              value={form.quantity_produced}
              onChange={(e) => setForm({ ...form, quantity_produced: e.target.value })}
              data-testid="edit-batch-produced"
            />
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="edit-batch-notes"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="submit" className="h-12 px-6" data-testid="edit-batch-submit">
            Save changes
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

function CreateBatchView({ recipes, defaultLocation, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    recipe_id: "",
    quantity_planned: 1,
    location_id: defaultLocation || null,
    notes: "",
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <ChefHat className="h-5 w-5" /> Start a new batch
        </DialogTitle>
      </DialogHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ ...form, quantity_planned: Number(form.quantity_planned) });
        }}
        className="space-y-4"
      >
        <div>
          <Label>Recipe</Label>
          <Select value={form.recipe_id} onValueChange={(v) => setForm({ ...form, recipe_id: v })}>
            <SelectTrigger className="h-12" data-testid="batch-recipe">
              <SelectValue placeholder="Pick recipe" />
            </SelectTrigger>
            <SelectContent>
              {[...recipes]
                .sort((a, b) =>
                  (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
                )
                .map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Planned quantity</Label>
          <Input
            type="number" step="0.01" className="h-12 mt-1"
            value={form.quantity_planned}
            onChange={(e) => setForm({ ...form, quantity_planned: e.target.value })}
            required data-testid="batch-planned"
          />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="submit" className="h-12 px-6" data-testid="batch-submit">Start batch</Button>
        </DialogFooter>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Record-ingredient-usage flow (scan-driven, one ingredient at a time).
//
// At dialog open we ONLY show the recipe's ingredients with their required
// quantity and a "Pending" pill. No inventory information is leaked until
// the operator scans an actual ingredient label — that scan identifies
// (a) which ingredient row to expand and (b) which intake batch to record
// against. The row then exposes the batch details + a quantity input pre-
// filled with the recipe-required amount. Pressing "Confirm" stamps the
// ingredient as done with a green tick. Repeat for each ingredient.
// ---------------------------------------------------------------------------

function StatusPill({ status }) {
  if (status === "done") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
        <CheckCircle2 className="h-3.5 w-3.5" /> Done
      </span>
    );
  }
  if (status === "scanning") {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold">
        <ScanLine className="h-3.5 w-3.5" /> Confirm
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-stone-100 text-stone-700 text-xs font-semibold">
      <ScanLine className="h-3.5 w-3.5" /> Scan label
    </span>
  );
}

function IngredientRow({
  ingredient,
  required,
  unit,
  status,         // "pending" | "scanning" | "done"
  scannedIntake,  // null when status !== "scanning" && !== "done"
  qty,
  substitution,        // boolean — scanned material differs from recipe slot
  actualMaterialName,  // name of the material actually scanned (for substitution)
  stockAutoAdjusted,   // boolean — system stock was 0/short at scan time
  onQtyChange,
  onConfirm,
  onCancel,
  onReopen,
}) {
  const isScanning = status === "scanning";
  const isDone = status === "done";

  return (
    <div
      className={`border rounded-lg p-4 transition-colors ${rowTone(isDone, isScanning)}`}
      data-testid={`ingredient-row-${ingredient.material_id}`}
      data-status={status}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-base">{ingredient.material_name}</div>
          <div className="text-xs text-stone-500 mt-0.5">
            Required for this batch:{" "}
            <span className="font-semibold tnum text-stone-700 dark:text-stone-200">
              {Number(required || 0).toFixed(2)}
            </span>{" "}
            {unit}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={status} />
          {isDone && (
            <button
              type="button"
              className="text-xs text-stone-500 underline-offset-2 hover:underline"
              onClick={onReopen}
              data-testid={`reopen-${ingredient.material_id}`}
            >
              Re-scan
            </button>
          )}
        </div>
      </div>

      {isScanning && scannedIntake && (
        <div
          className="mt-3 rounded-md border border-amber-300 bg-white dark:bg-stone-950 p-3"
          data-testid={`scan-detail-${ingredient.material_id}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Scanned batch
          </div>
          <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Internal</div>
              <div className="font-mono">{scannedIntake.internal_batch_code}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Supplier batch</div>
              <div className="font-mono">{scannedIntake.supplier_batch || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Supplier</div>
              <div>{scannedIntake.supplier_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Expiry</div>
              <div className="tnum">{scannedIntake.expiry_date?.slice(0, 10) || "—"}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Available on this bag</div>
              <div className="tnum font-semibold">
                {Number(scannedIntake.remaining || 0).toFixed(2)} {unit}
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-12 gap-2 items-end">
            <div className="col-span-7">
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">
                Quantity used
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                className="h-12 mt-1"
                value={qty || ""}
                onChange={(e) => onQtyChange(e.target.value)}
                data-testid={`qty-input-${ingredient.material_id}`}
                autoFocus
              />
            </div>
            <div className="col-span-5 flex gap-2">
              <Button
                type="button"
                variant="ghost"
                className="h-12 flex-1"
                onClick={onCancel}
                data-testid={`scan-cancel-${ingredient.material_id}`}
              >
                <XCircle className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                type="button"
                className="h-12 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={onConfirm}
                disabled={!(Number(qty) > 0)}
                data-testid={`scan-confirm-${ingredient.material_id}`}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {isDone && scannedIntake && (
        <div
          className="mt-2 text-xs text-emerald-800 font-medium"
          data-testid={`ingredient-done-summary-${ingredient.material_id}`}
        >
          {Number(qty).toFixed(2)} {unit} from{" "}
          <span className="font-mono">{scannedIntake.internal_batch_code}</span>
          {scannedIntake.supplier_batch && (
            <>
              {" "}· supplier batch{" "}
              <span className="font-mono">{scannedIntake.supplier_batch}</span>
            </>
          )}
          {substitution && (
            <div
              className="mt-1 inline-flex items-center gap-1 px-2 h-6 rounded-full bg-amber-100 text-amber-900 text-[11px] font-semibold"
              data-testid={`substitution-badge-${ingredient.material_id}`}
              title="Substituted from a different material in the same category"
            >
              ⇄ Substituted{actualMaterialName ? `: ${actualMaterialName}` : ""}
            </div>
          )}
          {stockAutoAdjusted && (
            <div
              className="mt-1 ml-1 inline-flex items-center gap-1 px-2 h-6 rounded-full bg-rose-100 text-rose-900 text-[11px] font-semibold"
              data-testid={`stock-adjusted-badge-${ingredient.material_id}`}
              title="System stock was zero or short — auto-adjusted from the physical scan"
            >
              ⚠ Stock auto-adjusted
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConsumeView({
  batch,
  recipe,
  intakes,
  materials = [],
  allowedSubstitutionCategories = [],
  onCancel,
  onSubmit,
  onPersistRow,
}) {
  const wedgeBuf = useRef("");
  const wedgeTimer = useRef(null);
  // Inline "last scan" status shown at the bottom of the dialog. We used
  // to fire a sonner toast for each scan but the toast overlay was
  // covering the "Record usage" button (iteration_25 bug #2). Keeping
  // the status inline means the operator can see it AND reach every
  // action button.
  const [lastScan, setLastScan] = useState(null); // null | {tone,msg}

  // Build the ingredient list as the source of truth for the UI. No inventory
  // metadata leaks until a scan identifies the specific batch.
  const recipeIngredients = recipe?.ingredients || [];
  const materialNames = {};
  intakes.forEach((it) => {
    if (it.material_id && it.material_name) {
      materialNames[it.material_id] = it.material_name;
    }
  });
  const nameFor = (ing) =>
    materialNames[ing.material_id] || ing.material_name || "Material";
  const nameForPrint = (ing) =>
    materials.find((m) => m.id === ing.material_id)?.name || nameFor(ing);

  // material_id → category lookup (lower-cased). Used to allow substitution
  // between materials that share a category (e.g. two flour brands).
  const catByMaterialId = {};
  materials.forEach((m) => {
    if (m?.id) {
      catByMaterialId[m.id] = String(m.category || "").trim().toLowerCase();
    }
  });
  const allowedCatSet = new Set(
    (allowedSubstitutionCategories || []).map((c) => String(c).trim().toLowerCase())
  );

  const scale = recipe?.yield_quantity
    ? (batch.quantity_planned || 0) / recipe.yield_quantity
    : 1;
  const requiredFor = (ing) => Number(((ing.quantity || 0) * scale).toFixed(2));

  // Per-ingredient state map keyed by material_id:
  //   { status: "pending" | "scanning" | "done",
  //     intakeId?: string,
  //     qty?: string,
  //     persisted?: boolean }   // already saved to the batch on the backend
  // Seeded from `batch.consumptions` so reopening a partially-recorded batch
  // shows already-confirmed ingredients as done (with their summary + Re-scan
  // link) instead of asking the operator to scan everything again. The
  // `persisted` flag prevents auto-save from re-submitting and double-counting
  // an ingredient that was loaded from the server.
  const [rows, setRows] = useState(() => {
    const init = {};
    const consumedByMat = {};
    (batch.consumptions || []).forEach((c) => {
      if (c.material_id) consumedByMat[c.material_id] = c;
    });
    recipeIngredients.forEach((ing) => {
      const prior = consumedByMat[ing.material_id];
      if (prior) {
        init[ing.material_id] = {
          status: "done",
          intakeId: prior.intake_id,
          qty: String(prior.quantity ?? ""),
          persisted: true,
          substitution: !!prior.substitution,
          actualMaterialName: prior.actual_material_name || null,
          stockAutoAdjusted: !!prior.stock_auto_adjusted,
        };
      } else {
        init[ing.material_id] = { status: "pending" };
      }
    });
    return init;
  });

  const handleScan = (code) => {
    const norm = String(code || "").trim();
    if (!norm) return;
    const match = intakes.find(
      (i) => i.internal_batch_code === norm || i.supplier_batch === norm
    );
    if (!match) {
      setLastScan({ tone: "error", msg: `No matching intake for "${norm}"` });
      return;
    }
    // Physical scan wins: even if system stock shows 0 or less than needed,
    // we trust the operator's scanned bag and record the usage. The backend
    // will auto-adjust `remaining` (allowing it to go negative for
    // reconciliation) and emit an audit entry so BRCGS drift can be reviewed.

    // 1) Exact-match: the scanned material is literally in the recipe.
    let ing = recipeIngredients.find((i) => i.material_id === match.material_id);
    let substitution = false;
    let substitutedFor = null;

    // 2) Category substitution fallback when no exact match is found.
    //    Pick the first *pending* recipe slot whose material shares the same
    //    category and whose category is on the admin allow-list.
    if (!ing) {
      const intakeCat = catByMaterialId[match.material_id];
      if (intakeCat && allowedCatSet.has(intakeCat)) {
        ing = recipeIngredients.find((slot) => {
          if (catByMaterialId[slot.material_id] !== intakeCat) return false;
          // Don't overwrite a slot that's already done.
          const cur = rows[slot.material_id];
          return !cur || cur.status !== "done";
        });
        if (ing) {
          substitution = true;
          substitutedFor = {
            material_id: ing.material_id,
            material_name: nameFor(ing),
            category: intakeCat,
          };
        }
      }
      if (!ing) {
        // Tell the operator *why* it was rejected so they understand.
        let msg;
        if (!intakeCat) {
          msg = `"${match.material_name}" is not in this recipe (no category set on it — ask admin to add one)`;
        } else if (!allowedCatSet.has(intakeCat)) {
          msg = `"${match.material_name}" is not in this recipe and category "${intakeCat}" is not on the substitution allow-list`;
        } else {
          msg = `"${match.material_name}" is not in this recipe and no compatible slot is open`;
        }
        setLastScan({ tone: "error", msg });
        return;
      }
    }

    const need = requiredFor(ing);
    // Take the recipe-required amount. We no longer clamp to remaining —
    // the physical scan is the source of truth and the backend records an
    // over-consumption delta so stock can be reconciled later.
    const take = need > 0 ? need : Number(match.remaining || 0);
    // Flag the row as auto-adjusted whenever the physical usage exceeds
    // what the system thinks is available (covers both remaining<=0 AND
    // partial short-stock, e.g. remaining=1 but need=5).
    const availableNow = Math.max(0, Number(match.remaining || 0));
    const stockAutoAdjusted = take > availableNow;
    // Auto-confirm: scan → done. The operator can still tap "Re-scan" on the
    // row to override the qty manually or switch to a different bag.
    setRows((r) => ({
      ...r,
      [ing.material_id]: {
        status: "done",
        intakeId: match.id,
        qty: take.toFixed(2),
        substitution,
        substitutedFor,
        actualMaterialName: match.material_name,
        stockAutoAdjusted,
      },
    }));
    const baseMsg = substitution
      ? `Substituted ${match.material_name} → ${substitutedFor.material_name} · ${take.toFixed(2)} ${ing.unit} from ${match.internal_batch_code}`
      : `Recorded ${match.material_name} · ${take.toFixed(2)} ${ing.unit} from ${match.internal_batch_code}`;
    setLastScan({
      tone: stockAutoAdjusted || substitution ? "warn" : "success",
      msg: stockAutoAdjusted
        ? `${baseMsg} · stock auto-adjusted (system showed ${availableNow.toFixed(2)})`
        : baseMsg,
    });
  };

  const setQty = (material_id, qty) => {
    setRows((r) => ({ ...r, [material_id]: { ...r[material_id], qty } }));
  };

  const confirmRow = async (material_id) => {
    // Local commit first so the UI shows "done" instantly.
    const current = rows[material_id];
    if (!current?.intakeId || !(Number(current?.qty) > 0)) {
      toast.error("Scan a label and enter a quantity first");
      return;
    }
    setRows((r) => ({ ...r, [material_id]: { ...r[material_id], status: "done" } }));
    // Auto-persist to the backend so the row survives a page reload /
    // navigation. We send one line; the backend appends and decrements
    // the intake. If persistence fails we revert the row to "scanning"
    // so the operator notices and can retry.
    try {
      await onPersistRow(batch.id, {
        intake_id: current.intakeId,
        quantity: Number(current.qty),
        recipe_material_id: material_id,
      });
      setRows((r) => ({
        ...r,
        [material_id]: { ...r[material_id], status: "done", persisted: true },
      }));
      toast.success("Ingredient saved", { duration: 1500 });
    } catch (err) {
      devLog("[consume] auto-persist failed:", err);
      // Roll the row back so the operator knows it didn't reach the
      // server; otherwise they'd see "done" + lose the data on reload.
      setRows((r) => ({
        ...r,
        [material_id]: { ...r[material_id], status: "scanning", persisted: false },
      }));
      toast.error(
        err?.response?.data?.detail || err?.message || "Save failed — re-confirm to retry",
      );
    }
  };

  const cancelRow = (material_id) => {
    setRows((r) => ({ ...r, [material_id]: { status: "pending" } }));
  };

  const reopenRow = (material_id) => {
    setRows((r) => ({
      ...r,
      [material_id]: { ...r[material_id], status: "scanning" },
    }));
  };

  // Keep a ref pointing at the *latest* handleScan so the keydown
  // listener below can call it without forcing a re-bind on every
  // intakes/recipe/rows change (which would clobber the wedge buffer).
  const handleScanRef = useRef(handleScan);
  useEffect(() => {
    handleScanRef.current = handleScan;
  });

  // ---- Bluetooth / USB keyboard-wedge scanner -------------------------------
  // Hand-held scanners (Bluetooth HID, USB) act as a keyboard: they emit the
  // scanned code as keystrokes followed by Enter. We buffer keystrokes that
  // (a) happen while no <input>/<textarea> has focus, and (b) arrive in quick
  // succession (≤ 60ms between keys) — i.e. faster than a human can type.
  //
  // We `preventDefault()` the flushing Enter so the browser doesn't also
  // activate whichever button happens to be focused (e.g. the "Scan label"
  // camera button), which was iteration_23 bug #1: wedge scans re-opening
  // the camera dialog.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "Enter") {
        const code = wedgeBuf.current.trim();
        wedgeBuf.current = "";
        if (code.length >= 3) {
          e.preventDefault();
          e.stopPropagation();
          handleScanRef.current(code);
        }
        return;
      }
      if (e.key.length === 1) {
        if (wedgeTimer.current) clearTimeout(wedgeTimer.current);
        wedgeBuf.current += e.key;
        wedgeTimer.current = setTimeout(() => {
          wedgeBuf.current = "";
        }, 250);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      if (wedgeTimer.current) clearTimeout(wedgeTimer.current);
    };
  }, []);

  const doneCount = recipeIngredients.filter(
    (i) => rows[i.material_id]?.status === "done"
  ).length;
  const totalCount = recipeIngredients.length;
  const allDone = doneCount === totalCount && totalCount > 0;

  const submit = () => {
    // Only send rows that haven't been auto-persisted yet — otherwise
    // the backend would append duplicate consumptions and double-deduct
    // the intake remaining.
    const lines = recipeIngredients
      .map((ing) => {
        const r = rows[ing.material_id];
        if (!r || r.status !== "done" || !r.intakeId || !(Number(r.qty) > 0)) {
          return null;
        }
        if (r.persisted) return null;
        return {
          intake_id: r.intakeId,
          quantity: Number(r.qty),
          recipe_material_id: ing.material_id,
        };
      })
      .filter(Boolean);
    if (lines.length === 0) {
      // Everything's already saved — let the operator close the dialog
      // and proceed to the next stage instead of nagging them.
      onSubmit([]);
      return;
    }
    if (lines.length < totalCount) {
      toast.warning(
        `Recording ${lines.length} of ${totalCount} ingredients — the rest will stay pending`
      );
    }
    onSubmit(lines);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display">
          Record ingredient usage · {batch.batch_code}
        </DialogTitle>
      </DialogHeader>
      <div className="flex items-center justify-between gap-2 pb-2 border-b border-border flex-wrap">
        <div className="text-xs text-stone-600">
          Planned <span className="font-semibold tnum">{fmt2(batch.quantity_planned)}</span> ·{" "}
          Recipe yield <span className="tnum">{fmt2(recipe?.yield_quantity)}</span>{" "}
          {recipe?.yield_unit || ""} · Scale{" "}
          <span className="font-semibold tnum">{scale.toFixed(2)}×</span> ·{" "}
          <span
            className={`font-semibold tnum ${
              allDone ? "text-emerald-700" : "text-stone-700"
            }`}
            data-testid="progress-counter"
          >
            {doneCount} / {totalCount} done
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 h-9 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold"
            data-testid="wedge-indicator"
            title="Bluetooth / USB scanner armed — scan anywhere on this dialog"
          >
            <Bluetooth className="h-3.5 w-3.5" /> Scanner ready
          </div>
          <PrintRecipeButton recipe={recipe} batch={batch} nameFor={nameForPrint} requiredFor={requiredFor} />
          <BarcodeScanner onScan={handleScan} label="Scan label" />
        </div>
      </div>
      <div
        className="max-h-[60vh] overflow-y-auto pr-2 space-y-3 mt-3"
        data-testid="consume-form"
      >
        {totalCount === 0 && (
          <div className="text-sm text-amber-700">This recipe has no ingredients defined.</div>
        )}
        {recipeIngredients.map((ing) => {
          const r = rows[ing.material_id] || { status: "pending" };
          // Prefer the live intake row (so remaining/expiry reflect current
          // stock). Fall back to the consumption record stored on the batch
          // so re-opened dialogs can still show a useful summary even when
          // the originating intake has been fully depleted.
          let scannedIntake = null;
          if (r.intakeId) {
            scannedIntake = intakes.find((i) => i.id === r.intakeId) || null;
            if (!scannedIntake) {
              const c = (batch.consumptions || []).find(
                (cc) => cc.intake_id === r.intakeId
              );
              if (c) {
                scannedIntake = {
                  id: c.intake_id,
                  internal_batch_code: c.internal_batch_code,
                  supplier_batch: c.supplier_batch,
                  supplier_name: c.supplier_name,
                  material_id: c.material_id,
                  material_name: c.material_name,
                  expiry_date: c.expiry_date,
                  remaining: 0,
                };
              }
            }
          }
          return (
            <IngredientRow
              key={ing.material_id}
              ingredient={{ ...ing, material_name: nameFor(ing) }}
              required={requiredFor(ing)}
              unit={ing.unit}
              status={r.status}
              scannedIntake={scannedIntake}
              qty={r.qty}
              substitution={!!r.substitution}
              actualMaterialName={r.actualMaterialName}
              stockAutoAdjusted={!!r.stockAutoAdjusted}
              onQtyChange={(q) => setQty(ing.material_id, q)}
              onConfirm={() => confirmRow(ing.material_id)}
              onCancel={() => cancelRow(ing.material_id)}
              onReopen={() => reopenRow(ing.material_id)}
            />
          );
        })}
      </div>
      {lastScan && (
        <div
          role="status"
          aria-live="polite"
          data-testid="scan-status"
          className={`mt-3 -mb-1 flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm font-medium ${SCAN_TONE[lastScan.tone]?.box || SCAN_TONE.error.box}`}
        >
          <span
            className={`mt-0.5 inline-block h-2.5 w-2.5 rounded-full ${SCAN_TONE[lastScan.tone]?.dot || SCAN_TONE.error.dot}`}
            aria-hidden="true"
          />
          <span className="flex-1">{lastScan.msg}</span>
          <button
            type="button"
            onClick={() => setLastScan(null)}
            aria-label="Dismiss last scan"
            data-testid="scan-status-dismiss"
            className="text-stone-500 hover:text-stone-800 -mt-0.5 -mr-1 h-6 w-6 rounded flex items-center justify-center"
          >
            ×
          </button>
        </div>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={submit}
          className="h-12 px-6"
          disabled={doneCount === 0}
          data-testid="consume-submit"
        >
          Record usage{allDone ? "" : ` (${doneCount}/${totalCount})`}
        </Button>
      </DialogFooter>
    </>
  );
}

const OVEN_OPTIONS = [
  "Rotary oven",
  "Deck oven gas",
  "Deck oven electric",
];

function BakeView({ batch, onCancel, onSubmit }) {
  const [bake, setBake] = useState({
    oven_id: batch.bake?.oven_id || "",
    bake_temp_c: batch.bake?.bake_temp_c || "",
    bake_minutes: batch.bake?.bake_minutes || "",
    notes: "",
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <Flame className="h-5 w-5 text-amber-600" /> Bake record · {batch.batch_code}
        </DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Oven</Label>
          <Select
            value={bake.oven_id}
            onValueChange={(v) => setBake({ ...bake, oven_id: v })}
          >
            <SelectTrigger className="h-12 mt-1" data-testid="bake-oven">
              <SelectValue placeholder="Select an oven" />
            </SelectTrigger>
            <SelectContent>
              {OVEN_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`bake-oven-opt-${o.replace(/\s+/g, "-").toLowerCase()}`}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Temperature (°C)</Label>
          <Input type="number" className="h-12 mt-1" value={bake.bake_temp_c} onChange={(e) => setBake({ ...bake, bake_temp_c: e.target.value })} data-testid="bake-temp" />
        </div>
        <div>
          <Label>Time (minutes)</Label>
          <Input type="number" className="h-12 mt-1" value={bake.bake_minutes} onChange={(e) => setBake({ ...bake, bake_minutes: e.target.value })} data-testid="bake-time" />
        </div>
        <div className="col-span-2">
          <Label>Notes</Label>
          <Textarea value={bake.notes} onChange={(e) => setBake({ ...bake, notes: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() =>
            onSubmit({
              ...bake,
              bake_temp_c: bake.bake_temp_c ? Number(bake.bake_temp_c) : null,
              bake_minutes: bake.bake_minutes ? Number(bake.bake_minutes) : null,
            })
          }
          className="h-12 px-6"
          data-testid="bake-submit"
        >
          Save bake
        </Button>
      </DialogFooter>
    </>
  );
}

function CompleteView({ batch, onCancel, onSubmit }) {
  const [qty, setQty] = useState(String(batch.quantity_planned || ""));
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" /> Complete batch · {batch.batch_code}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <Label>Quantity actually produced</Label>
        <Input
          type="number" step="0.01" className="h-12"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          data-testid="complete-qty"
        />
        <div className="text-sm text-stone-500">Planned: {fmt2(batch.quantity_planned)}</div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(Number(qty || 0))} className="h-12 px-6" data-testid="complete-submit">
          Complete batch
        </Button>
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page (orchestrator)
// ---------------------------------------------------------------------------

export default function Production() {
  const { params, selected } = useLocations();
  const [batches, setBatches] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [allowedCats, setAllowedCats] = useState([]);
  const [dialog, setDialog] = useState(null); // null | { view, batch? }
  const [pinPrompt, setPinPrompt] = useState(null); // null | { title, description, run }
  const [scope, setScope] = useState({ mode: "today" });

  const load = async () => {
    const [b, r, i, m, s] = await Promise.all([
      api.get("/batches", { params: { ...params(), ...scopeToParams(scope) } }),
      api.get("/recipes"),
      api.get("/intakes", { params: params() }),
      api.get("/materials"),
      api.get("/settings/substitution-categories"),
    ]);
    setBatches(b.data);
    setRecipes(r.data);
    setIntakes(i.data);
    setMaterials(m.data);
    setAllowedCats(
      (s.data?.categories || []).map((c) => String(c).trim().toLowerCase())
    );
  };
  useEffect(() => {
    load();
    // `load` is recreated each render but reads `params()` and `scope`.
    // We refetch whenever either changes — including the Today/History
    // toggle and date range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, scope]);

  const closeDialog = () => setDialog(null);

  const apiCall = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      closeDialog();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleCreate = (form) =>
    apiCall(
      () => api.post("/batches", { ...form, location_id: form.location_id || selected || null }),
      "Production batch started"
    );

  const handleConsume = (lines) =>
    apiCall(
      () => api.post(`/batches/${dialog.batch.id}/consume`, { consumptions: lines }),
      "Ingredients recorded"
    );

  // Per-row auto-persistence — called by the ConsumeView as soon as
  // each ingredient is confirmed. Reads `batchId` from the caller
  // (rather than `dialog?.batch?.id`) so it never falls into a
  // closure / stale-state race where `dialog` looks momentarily null.
  // Throws on failure so ConsumeView can revert the UI to "scanning".
  const handlePersistRow = async (batchId, line) => {
    if (!batchId) {
      devLog("[consume] persist called without batchId");
      throw new Error("Missing batch id");
    }
    if (!line || !line.intake_id || !(line.quantity > 0)) {
      throw new Error("Invalid line");
    }
    const { data } = await api.post(
      `/batches/${batchId}/consume`,
      { consumptions: [line] },
    );
    // Replace the dialog's batch with the freshly persisted server
    // copy so consumptions[] is up to date if the user reopens the
    // dialog later or hits Submit on remaining rows.
    setDialog((d) => (d && d.batch?.id === batchId ? { ...d, batch: data } : d));
    load();
    return data;
  };

  const handleBake = (bakeBody) =>
    apiCall(() => api.post(`/batches/${dialog.batch.id}/bake`, bakeBody), "Bake recorded");

  const handleComplete = async (qty) => {
    try {
      const { data } = await api.post(`/batches/${dialog.batch.id}/complete`, {
        quantity_produced: qty,
      });
      const bits = [];
      if (data.auto_packing_id) {
        bits.push(`${Math.round(Number(qty))} packs sent to inventory`);
      }
      if (data.shortfall_waste_id) {
        const planned = Number(dialog.batch.quantity_planned || 0);
        const shortfall = Math.max(0, planned - Number(qty || 0));
        bits.push(`${shortfall.toFixed(2)} auto-logged as production waste`);
      }
      toast.success(
        bits.length ? `Batch completed · ${bits.join(" · ")}` : "Batch completed"
      );
      closeDialog();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleEditSubmit = (patch) =>
    apiCall(() => api.patch(`/batches/${dialog.batch.id}`, patch), "Batch updated");

  // PIN-locked entry points — request supervisor PIN (admin OR dispatcher 3333)
  // before opening the edit dialog / firing the delete request.
  const requestEdit = (b) =>
    setPinPrompt({
      title: "Unlock batch edit",
      description: `Enter supervisor PIN to edit ${b.batch_code}.`,
      run: () => setDialog({ view: "edit", batch: b }),
    });

  const requestDelete = (b) =>
    setPinPrompt({
      title: "Confirm batch deletion",
      description: `Enter supervisor PIN to permanently delete ${b.batch_code}.`,
      run: () =>
        apiCall(() => api.delete(`/batches/${b.id}`), `Batch ${b.batch_code} deleted`),
    });

  const activeRecipe = recipes.find((r) => r.id === dialog?.batch?.recipe_id);
  const [carryOverDismissed, setCarryOverDismissed] = useState(false);

  return (
    <div>
      <PageHeader title="Production Batches" eyebrow="Production · Daily mixing & baking">
        <Button
          className="h-12 px-5 gap-2"
          onClick={() => setDialog({ view: "create" })}
          data-testid="new-batch-button"
        >
          <Plus className="h-5 w-5" /> Start batch
        </Button>
      </PageHeader>

      <CarryOverPanel
        dismissed={carryOverDismissed}
        onDismiss={() => setCarryOverDismissed(true)}
        locationId={selected}
      />

      <Panel
        title={`Batches (${batches.length})`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <DateScopePicker value={scope} onChange={setScope} />
            {scope.mode === "history" && batches.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadCsv(
                    `batches-${scope.from_date}-to-${scope.to_date}.csv`,
                    batches,
                    [
                      { header: "Batch code", value: "batch_code" },
                      { header: "Recipe", value: "recipe_name" },
                      { header: "Stage", value: "stage" },
                      { header: "Planned", value: "quantity_planned" },
                      { header: "Produced", value: "quantity_produced" },
                      { header: "Started", value: "started_at" },
                      { header: "Finished", value: "finished_at" },
                      { header: "Started by", value: "created_by_name" },
                    ]
                  )
                }
                data-testid="batches-export-csv"
              >
                Export CSV
              </Button>
            )}
          </div>
        }
      >
        <BatchesTable
          batches={batches}
          recipes={recipes}
          onStage={(b, view) => setDialog({ view, batch: b })}
          onEdit={requestEdit}
          onDelete={requestDelete}
        />
      </Panel>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl" data-testid="batch-dialog">
          {dialog?.view === "create" && (
            <CreateBatchView
              recipes={recipes}
              defaultLocation={selected}
              onCancel={closeDialog}
              onSubmit={handleCreate}
            />
          )}
          {dialog?.view === "consume" && dialog.batch && (
            <ConsumeView
              batch={dialog.batch}
              recipe={activeRecipe}
              intakes={intakes}
              materials={materials}
              allowedSubstitutionCategories={allowedCats}
              onCancel={closeDialog}
              onSubmit={handleConsume}
              onPersistRow={handlePersistRow}
            />
          )}
          {dialog?.view === "bake" && dialog.batch && (
            <BakeView batch={dialog.batch} onCancel={closeDialog} onSubmit={handleBake} />
          )}
          {dialog?.view === "complete" && dialog.batch && (
            <CompleteView batch={dialog.batch} onCancel={closeDialog} onSubmit={handleComplete} />
          )}
          {dialog?.view === "edit" && dialog.batch && (
            <EditBatchView batch={dialog.batch} onCancel={closeDialog} onSubmit={handleEditSubmit} />
          )}
        </DialogContent>
      </Dialog>

      <SupervisorPinModal
        open={!!pinPrompt}
        title={pinPrompt?.title}
        description={pinPrompt?.description}
        roles={["admin", "dispatcher"]}
        onClose={() => setPinPrompt(null)}
        onVerified={() => {
          const fn = pinPrompt?.run;
          setPinPrompt(null);
          fn?.();
        }}
      />
    </div>
  );
}
